import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  Tooltip
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { IPractitioner } from "../types";
import { Dispatch, SetStateAction, useEffect, useState, useMemo, useRef } from "react";
import { useFormatter } from "../../../utils/useFormatter";
import { GetPractitionerRole } from "../../../services/fhir";

interface IProps {
  slotType: any;
  practitioners: IPractitioner[] | undefined;
  selectedPractitioner: number;
  setSelectedPractitioner: Dispatch<SetStateAction<number>>;
  organization: any;
}

const PRACTITIONERS_PER_PAGE = 3;

function PractitionerSkeleton() {
  return (
    <>
      {Array.from({ length: PRACTITIONERS_PER_PAGE }).map((_, i) => (
        <Card
          key={`practitioner-skeleton-${i}`}
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="circular" width={40} height={40} />
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rounded" width={80} height={24} />
                <Skeleton variant="rounded" width={60} height={24} />
              </Stack>
            </Stack>
          </Stack>
        </Card>
      ))}
    </>
  );
}

export function PractitionersOptions({
  organization,
  practitioners,
  selectedPractitioner,
  setSelectedPractitioner,
}: IProps) {
  const [page, setPage] = useState(0);
  const [cardData, setCardData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const didAutoSelectRef = useRef(false);

  useEffect(() => {
    setPage(0);
    // Quando o dataset de profissionais muda (nova busca/novo card), queremos
    // auto-selecionar novamente (sempre o primeiro da lista ordenada).
    didAutoSelectRef.current = false;
  }, [practitioners]);

  useEffect(() => {
    let isMounted = true;

    async function fetchSpecialties() {
      if (!practitioners?.length) {
        if (isMounted) setCardData([]);
        return;
      }
      
      setIsLoading(true);
      try {
        const ids = practitioners.map((el: any) => el?.reference).join(",");
        const roles = await GetPractitionerRole({ practitioner: ids });

        if (!isMounted) return;

        const findRoles = practitioners.map((el: any, originalIndex: number) => {
          const matchingEntries = roles?.entry?.filter(
            (e: any) =>
              e?.resource?.practitioner?.reference === el?.reference &&
              e?.resource?.organization?.reference?.includes(
                organization?.resource?.id
              )
          );

          const specialties = matchingEntries
            ?.map((e: any) => e?.resource?.specialty)
            ?.flatMap((item: any) => item);

          return { ...el, specialties, originalIndex };
        });

        const sortedRoles = findRoles.sort((a: any, b: any) => {
          const nameA = a?.display?.toLowerCase() || "";
          const nameB = b?.display?.toLowerCase() || "";
          return nameA.localeCompare(nameB);
        });

        setCardData(sortedRoles);
      } catch (error) {
        console.error("Erro ao buscar especialidades:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    fetchSpecialties();

    return () => {
      isMounted = false;
    };
  }, [practitioners, organization]);

  useEffect(() => {
    if (!cardData?.length) return;

    // Auto-seleção: sempre seleciona o primeiro item (já ordenado) ao carregar
    // um novo dataset. Evita "pré-selecionar o último" quando existia seleção
    // anterior que cai em outra página.
    if (didAutoSelectRef.current) return;

    const first = cardData[0];
    const nextIndex =
      typeof first?.originalIndex === "number" ? first.originalIndex : 0;

    didAutoSelectRef.current = true;
    setPage(0);
    setSelectedPractitioner(nextIndex);
  }, [cardData, selectedPractitioner, setSelectedPractitioner]);

  const { visiblePractitioners, totalPages } = useMemo(() => {
    const total = Math.ceil((cardData?.length || 0) / PRACTITIONERS_PER_PAGE);
    const startIndex = page * PRACTITIONERS_PER_PAGE;
    const endIndex = startIndex + PRACTITIONERS_PER_PAGE;
    const visible = cardData?.slice(startIndex, endIndex) || [];
    
    return { visiblePractitioners: visible, totalPages: total };
  }, [cardData, page]);

  const showSkeleton = isLoading || (!cardData?.length && practitioners?.length);

  return (
    <Stack spacing={1.5} mt={2} mb={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="body2"
          color="neutral700.main"
          textAlign="left"
          fontWeight={500}
        >
          Escolha um dos profissionais disponíveis:
        </Typography>

        {cardData.length > PRACTITIONERS_PER_PAGE && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => setPage((prev) => prev - 1)}
              disabled={page === 0}
              sx={{ padding: "4px" }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: "40px", textAlign: "center" }}
            >
              {page + 1} / {totalPages}
            </Typography>

            <IconButton
              size="small"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={page >= totalPages - 1}
              sx={{ padding: "4px" }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Stack
        direction="column"
        spacing={1}
        sx={{ width: "100%", minHeight: "200px" }}
      >
        {showSkeleton ? (
          <PractitionerSkeleton />
        ) : (
          visiblePractitioners.map((el: any, index: number) => {
            const startIndex = page * PRACTITIONERS_PER_PAGE;
            const originalIndex =
              typeof el?.originalIndex === "number"
                ? el.originalIndex
                : startIndex + index;

            const isSelected = selectedPractitioner === originalIndex;
            const hasCost = Number(el?.serviceCost) > 0;

            return (
              <Card
                key={el.reference || originalIndex}
                variant="outlined"
                component={Button}
                onClick={() => setSelectedPractitioner(originalIndex)}
                aria-pressed={isSelected}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  backgroundColor: isSelected
                    ? "action.selected"
                    : "background.paper",
                  borderColor: isSelected ? "primary.main" : "divider",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Avatar
                    sx={(theme) => ({
                      width: 40,
                      height: 40,
                      backgroundColor: theme.palette.action.hover,
                      color: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      fontSize: "16px",
                      fontWeight: 700,
                      mt: 0.5
                    })}
                  >
                    {useFormatter?.formatNameFirstInitials(el.display || "", 2)}
                  </Avatar>

                  <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={isSelected ? "primary.main" : "text.primary"}
                      noWrap
                      sx={{ width: "100%", lineHeight: 1.2 }}
                      title={el.display}
                    >
                      Dr(a). {el?.display}
                    </Typography>

                    <Box 
                      sx={{ 
                        display: "flex", 
                        flexWrap: "wrap", 
                        gap: 0.75, 
                        alignItems: "center",
                        maxWidth: "100%"
                      }}
                    >
                      {el?.healthcare?.display && (
                        <Tooltip title={el.healthcare.display}>
                          <Chip
                            label={el.healthcare.display}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: "20px",
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              borderColor: "divider",
                              backgroundColor: "background.paper",
                              "& .MuiChip-label": {
                                paddingLeft: "6px",
                                paddingRight: "6px"
                              }
                            }}
                          />
                        </Tooltip>
                      )}

                      {hasCost && (
                        <Chip
                          label={useFormatter?.formatCurrency(
                            Number(el.serviceCost) * 100
                          )}
                          size="small"
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            height: "20px",
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            borderColor: "divider",
                            "& .MuiChip-label": {
                                paddingLeft: "6px",
                                paddingRight: "6px"
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Card>
            );
          })
        )}
      </Stack>
    </Stack>
  );
}
